import React, { useEffect, useState } from 'react';
import './CallOverlay.scss';
import { LOCAL_STORAGE } from 'constants/appConstants';
import { getLocalStorageItem } from 'utils/localStorage';

const CallOverlay: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkCallState = () => {
      try {
        // Check if Amazon Connect widget iframe is present in this tab
        const widgetIframe = document.querySelector(
          'iframe[title*="Amazon Connect"], iframe[id*="amazon-connect"]',
        );
        const hasWidget = !!widgetIframe;

        // Check if call is active in any tab via localStorage
        const callState = getLocalStorageItem(LOCAL_STORAGE.AWS_ACTIVE_CALL);
        const hasActiveCall = callState && callState.isActive;

        // Show overlay if widget exists OR if call is active in any tab
        const shouldShow = hasWidget || hasActiveCall;

        if (shouldShow !== isVisible) {
          setIsVisible(shouldShow);
          // Block body scroll when overlay is shown
          if (shouldShow) {
            document.body.style.overflow = 'hidden';
          } else {
            document.body.style.overflow = '';
          }
        }
      } catch (error) {
        console.error('[CallOverlay] Error checking call state:', error);
      }
    };

    // Check immediately
    checkCallState();

    // Check periodically for widget presence and localStorage state
    const interval = setInterval(checkCallState, 500);

    // Also listen for DOM changes (MutationObserver for faster detection)
    const observer = new MutationObserver(checkCallState);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Listen for storage events (cross-tab synchronization)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LOCAL_STORAGE.AWS_ACTIVE_CALL) {
        console.log('[CallOverlay] Storage event detected:', {
          oldValue: e.oldValue,
          newValue: e.newValue,
        });
        checkCallState();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      observer.disconnect();
      window.removeEventListener('storage', handleStorageChange);
      // Restore body scroll on cleanup
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="call-overlay">
      <div className="call-overlay-backdrop" />
      <div className="call-overlay-content">
        <div className="call-overlay-message">
          <div className="call-overlay-spinner" />
          <p>Call in progress...</p>
        </div>
      </div>
    </div>
  );
};

export default CallOverlay;
